/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2023, 2025
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'react',
	'prop-types',
	'../WidthScaler',
	'./PrintScaleTableContext',
	'../PrintHelper'
], (
	React,
	PropTypes,
	WidthScaler,
	PrintScaleTableContext,
	PrintHelper
) => {

	const RENDER_SUPPORT_VALUES = {
		expansionLineageHintSupported: false
	};
	/**
	 * A React component which renders the table structure of a table or crosstab for printing
	 */

	/**
 	 * There are different parts of a crosstab, namely the "corner", the
 	 * "fixed columns" (i.e. row headings), the "fixed rows" (i.e. column headings) and the data.
 	 *
 	 * |             |           <Fixed Rows>            |
 	 * |  <Corner>   |    ...    |    ...    |    ...    |
 	 * |             | ... | ... | ... | ... | ... | ... |
 	 * |-------------|-----|-----|-----|-----|-----|-----|
 	 * | <  Fixed  > | ... | ... | ### | ### | ### | ### |
 	 * | < Columns > |     | ... | ### | ### | ### | ### |
 	 * |             |-----|-----|-----|-----|-----|-----|
 	 * |             | ... | ... | ### |   <Data>  | ### |
 	 * |             |     | ... | ### | ### | ### | ### |
 	 * |             |-----|-----|-----|-----|-----|-----|
 	 * |             | ... | ... | ### | ### | ### | ### |
 	 * |             |     | ... | ### | ### | ### | ### |
 	 *
 	 * In crosstabs, cells are merged in various ways, namely:
 	 *
 	 * 1) Corner - The corner is always exactly one cell, so its rowSpan is the number of rows in
 	 * the fixed rows, and its colSpan is the number of columns in the fixed columns.
 	 *
 	 * 2) Fixed rows and columns - There are two sorts of merging in the fixed rows and columns:
 	 *    a) Nesting. In hierarchical rendering, parents merge the full breadth of their children.
 	 *       So, in fixed rows, they have a row span, e.g.
 	 *
 	 *       |               2007              |               2008              |
 	 *       | Spring | Summer | Fall | Winter | Spring | Summer | Fall | Winter |
 	 *
 	 *       And, in fixed columns they have a col span, e.g.
 	 *
 	 *       |      | Spring |
 	 *       | 2007 | Summer |
 	 *       |      | Fall   |
 	 *       |      | Winter |
 	 *       |------|--------|
 	 *       |      | Spring |
 	 *       | 2008 | Summer |
 	 *       |      | Fall   |
 	 *       |      | Winter |
 	 *
 	 *    b) Summaries. These merge across nesting levels. So, in fixed rows, they have a col span,
 	 *       e.g.
 	 *
 	 *       2010                         | Summary |
 	 *       |----------------------------|         |
 	 *       Winter             | Summary |         |
 	 *       -------------------|         |         |
 	 *       | Friday | Summary |         |         |
 	 *       |--------|---------|---------|---------|
 	 *
 	 *       And likewise, in fixed columns, they have a row span, e.g.
 	 *
 	 *       | 2010 | Winter |---------|
 	 *       |      |        | Friday  |
 	 *       |      |        |---------|
 	 *       |      |        | Summary |
 	 *       |      |--------|---------|
 	 *       |      | Summary          |
 	 *       |------|------------------|
 	 *       | Summary                 |
 	 *       |-------------------------|
 	 *
 	 * 3) Data - there is no merging of data cells.
 	 *
 	 */
	return class PrintTableGridComponent extends React.Component {
		static propTypes = {
			fixedCornerCellRenderer: PropTypes.func.isRequired,
			fixedRowCellRenderer: PropTypes.func.isRequired,
			fixedColumnCellRenderer: PropTypes.func.isRequired,
			dataCellRenderer: PropTypes.func.isRequired,
			fixedRows: PropTypes.number.isRequired,
			fixedColumns: PropTypes.number.isRequired,
			numRows: PropTypes.number.isRequired,
			numColumns: PropTypes.number.isRequired,
			rowHeight: PropTypes.func.isRequired,
			columnWidth: PropTypes.func.isRequired,
			style: PropTypes.object.isRequired,
			pageSize: PropTypes.shape({
				width: PropTypes.number,
				height: PropTypes.number
			}),
			content: PropTypes.object.isRequired,
			renderCell: PropTypes.func.isRequired,
			getTableRows: PropTypes.func.isRequired
		}

		static getRenderSupportFlag(key) {
			return RENDER_SUPPORT_VALUES[key];
		}

		constructor(props) {
			super(props);
			this._scaleTableRef = [];
			this._initialTableRef = React.createRef();
			this._scaleFactor = 1.0;
			this._titleHeight = 0.0; // A crosstab may have a title
			this._columnHeadersHeight = 0.0; // A crosstab may have column header rows
			this.state = {
				rowHeights: undefined
			};
			this._initializeRenderers();
		}

		recomputeGridSize() { }

		componentDidMount () {
			if (this._initialTableRef.current) {
				const tableElement = this._initialTableRef.current;
				const { columnHeadersHeight, rowHeights, scaleFactor, titleHeight } = this._getDimensionScaleInfo({ tableElement });
				this._columnHeadersHeight = columnHeadersHeight;
				this._titleHeight = titleHeight;
				this._scaleFactor = scaleFactor;
				if (rowHeights.length) {
					this.setState({ rowHeights: [...rowHeights] });
				}
			}
		}

		render() {
			const { rowHeights } = this.state;
			const initialTableRequired = typeof rowHeights === 'undefined';
			return initialTableRequired ? this._renderInitialTable() : this._renderTableBaseOnScaleFactor();
		}

		_initializeRenderers() {
			const { rowHeight, columnWidth, fixedRows, fixedColumns, fixedCornerCellRenderer, fixedRowCellRenderer, fixedColumnCellRenderer, dataCellRenderer, renderCell } = this.props;
			this._renderers = {
				cornerCell: ({ rowIndex, colIndex }) => {
					return renderCell({
						renderer: fixedCornerCellRenderer,
						rowIndex,
						colIndex,
						props: {
							style: {
								width: columnWidth(colIndex),
								height: rowHeight(rowIndex),
								left: 0,
								top: 0
							}
						}
					});
				},
				rowHeaderCell: ({ rowIndex, colIndex }) => {
					const currentRow = rowIndex - fixedRows;
					return renderCell({
						renderer: fixedColumnCellRenderer,
						rowIndex: currentRow,
						colIndex,
						props: {
							style: {
								width: columnWidth(colIndex),
								height: rowHeight(currentRow + fixedRows),
								left: 0,
								top: 0
							}
						}
					});
				},
				columnHeaderCell: ({ rowIndex, colIndex }) => {
					const currentColumn = colIndex - fixedColumns;
					return renderCell({
						renderer: fixedRowCellRenderer,
						rowIndex,
						colIndex: currentColumn,
						props: {
							style: {
								width: columnWidth(currentColumn + fixedColumns),
								height: rowHeight(rowIndex),
								left: 0,
								top: 0
							}
						}
					});
				},
				dataCell: ({ rowIndex, colIndex }) => {
					const currentColumn = colIndex - fixedColumns;
					const currentRow = rowIndex - fixedRows;
					return renderCell({
						renderer: dataCellRenderer,
						rowIndex: currentRow,
						colIndex: currentColumn,
						props: {
							style: {
								width: columnWidth(currentColumn + fixedColumns),
								height: rowHeight(currentRow + fixedRows),
								left: 0,
								top: 0
							}
						}
					});
				}
			};
		}

		_getFunctionName({ rowIndex, colIndex, fixedRows, fixedColumns }) {
			if (colIndex < fixedColumns && rowIndex < fixedRows) {
				return this._renderers.cornerCell.name;
			} else if(colIndex < fixedColumns) {
				return this._renderers.rowHeaderCell.name;
			} else if(rowIndex < fixedRows) {
				return this._renderers.columnHeaderCell.name;
			}
			// Otherwise render data cell
			return this._renderers.dataCell.name;
		}

		_renderInitialTable() {
			const { style } = this.props;
			// Render columnHeaders
			const rows = [...this._renderColumnHeaders()];
			// Add all table rows
			this._getTableRows().forEach(row => {
				rows.push(...row);
			});
			return (
				<div className="print-table-container">
					<table
						ref={this._initialTableRef}
						className="print-table"
						border="0"
						cellPadding="0"
						cellSpacing="0"
						style={{ ...style }}
					>
						{rows}
					</table>
				</div>
			);
		}

		_renderTableBaseOnScaleFactor() {
			const tables = this._renderTablesBaseOnScaleFactor();
			return (
				<div className="scaled-container">
					{tables}
				</div>
			);
		}

		_renderTable({ tableRows, height }) {
			const { pageSize } = this.props;
			const { width: maxWidth } = pageSize;
			const key = this._scaleTableRef.length;
			this._scaleTableRef[key] =  React.createRef();
			const { style } = this.props;
			const styleUse = style || {};
			styleUse.height = height;
			return (
				<WidthScaler
					getNode={() => this._scaleTableRef[key].current}
					maxWidth={maxWidth}
					scale={this._scaleFactor}
					key={key}
				>
					<table
						ref={this._scaleTableRef[key]}
						className="print-table"
						border="0"
						cellPadding="0"
						cellSpacing="0"
						style={{ ...styleUse }}
					>
						{this._columnHeadersHeight > 0 && this._renderColumnHeaders()}
						{tableRows}
					</table>
				</WidthScaler>
			);
		}

		_renderColumnHeaders() {
			const { fixedRows, fixedColumns, numColumns } = this.props;
			const rows = [];
			for (let rowIndex = 0; rowIndex < fixedRows; rowIndex++) {
				const rowCells = [];
				for (let colIndex = 0; colIndex < numColumns + fixedColumns; colIndex++) {
					const fnName = this._getFunctionName({ rowIndex, colIndex, fixedRows, fixedColumns });
					const { content, attributes } = this._renderers[fnName]({ rowIndex, colIndex });
					if (content) {
						rowCells.push(
							<td {...attributes} key={colIndex}>
								{content}
							</td>
						);
					}
				}
				rows.push(
					<tr key={rowIndex}>
						{rowCells}
					</tr>
				);
			}
			return rows;
		}

		_getTableRows () {
			const { getTableRows } = this.props;
			const renderer = ({ rowIndex, colIndex, fixedRows, fixedColumns }) => {
				const fnName = this._getFunctionName({ rowIndex, colIndex, fixedRows, fixedColumns });
				return this._renderers[fnName]({ rowIndex, colIndex });
			};
			return getTableRows({ renderer });
		}

		_getDimensionScaleInfo({ tableElement }) {
			const { fixedRows, numRows, pageSize, content } = this.props;
			let titleHeight = 0.0;
			let columnHeadersHeight = 0.0;
			const rowHeights = [];
			const widgetContentWrapper = tableElement.closest('.widgetContentWrapper');
			if (widgetContentWrapper) {
				const widgetContentWrapperRect = widgetContentWrapper.getBoundingClientRect();
				const tableRect = tableElement.getBoundingClientRect();
				let height = tableRect.top - widgetContentWrapperRect.top;
				if (height < 0.0) {
					height = 0.0;
				}
				titleHeight = height;
			}
			// Get full size of content
			const naturalSize = tableElement.getBoundingClientRect();
			// Scale base on width or height where scale base on width take precedence over scale base on height
			const scaleFactor = PrintHelper.getScaleFactor({ content, pageSizeWidth: pageSize.width, naturalSizeWidth: naturalSize.width });
			// Get full size of content
			const naturalRows = Array.from(tableElement.querySelectorAll('tr'));
			// Should not be in situation where naturalRows.length !== numRows + fixedRows (fixedRows = # of rows for column headers)
			// However check here just to be safe since during render it is assumed that naturalRows.length === numRows
			if (naturalRows.length === numRows + fixedRows) {
				naturalRows.forEach((naturalRow, row) => {
					const rowSize = naturalRow.getBoundingClientRect();
					if (row < fixedRows) {
						columnHeadersHeight += rowSize.height;
					} else {
						rowHeights.push(rowSize.height);
					}
				});
			}
			return {
				columnHeadersHeight,
				rowHeights,
				scaleFactor,
				titleHeight
			};
		}

		_renderTablesBaseOnScaleFactor() {
			const { pageSize } = this.props;
			const { rowHeights } = this.state;
			const rowsGroupedByRowSpans = this._getTableRows();
			// A table has a table title and a set of column header rows
			// The height increase by the scale factor that can hold the rows
			// So if page.height = 100, scale = 0.25 then maxTableHeight = 400
			// More rows can squeeze into height of 400 to fill vertically after the table scaled down to 25% widthwise
			const context = new PrintScaleTableContext({
				columnHeadersHeight: this._columnHeadersHeight,
				maxTableHeight: pageSize.height/this._scaleFactor,
				renderRowHeaderCell: this._renderers.rowHeaderCell,
				renderTable: this._renderTable.bind(this),
				rowHeights,
				titleHeight: this._titleHeight
			});
			return context.getPrintTables({ rowsGroupedByRowSpans });
		}
	};
});
